import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Flag } from 'lucide-react';
import { cn } from '@/lib/utils';

const MENU_WIDTH = 140;

export const priorityOptions: Array<{ value: string; label: string; color: string }> = [
  { value: 'URGENT', label: 'Urgent', color: '#EF4444' },
  { value: 'HIGH', label: 'High', color: '#F97316' },
  { value: 'MEDIUM', label: 'Medium', color: '#3B82F6' },
  { value: 'LOW', label: 'Low', color: '#9CA3AF' },
];

export interface PriorityPickerProps {
  priority?: string;
  onChange?: (priority: string) => void;
  stopDragPointerDown?: boolean;
}

export function PriorityPicker({ priority, onChange, stopDragPointerDown = true }: PriorityPickerProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const current = priorityOptions.find((p) => p.value === priority) ?? priorityOptions[3];
  const editable = !!onChange;

  const updatePosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const rightEdge = rect.left + MENU_WIDTH;
    const left =
      rightEdge > window.innerWidth - 8
        ? Math.max(8, window.innerWidth - MENU_WIDTH - 8)
        : rect.left;
    setPos({ top: rect.bottom + 4, left });
  };

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => updatePosition();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleOutside(e: MouseEvent) {
      const t = e.target as Node;
      if (
        menuRef.current && !menuRef.current.contains(t) &&
        buttonRef.current && !buttonRef.current.contains(t)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const chipContent = (
    <>
      <Flag className="w-3 h-3" style={{ color: current.color, fill: current.color }} />
      <span>{current.label}</span>
    </>
  );

  if (!editable) {
    return (
      <span
        className="flex items-center gap-1 flex-shrink-0 px-1.5 py-0.5 text-secondary"
        title={`Priority: ${current.label}`}
      >
        {chipContent}
      </span>
    );
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onPointerDown={stopDragPointerDown ? (e) => e.stopPropagation() : undefined}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="flex items-center gap-1 flex-shrink-0 rounded px-1.5 py-0.5 transition-colors hover:bg-grey-100 text-secondary"
        title={`Priority: ${current.label}`}
      >
        {chipContent}
      </button>

      {open && pos && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: MENU_WIDTH }}
          className="bg-white rounded-lg shadow-elevated border border-grey-200 py-1 z-50"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {priorityOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(option.value);
                setOpen(false);
              }}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-grey-50 transition-colors text-secondary',
                priority === option.value && 'font-medium',
              )}
            >
              <Flag
                className="w-3 h-3 flex-shrink-0"
                style={{ color: option.color, fill: option.color }}
              />
              {option.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
